import React from "react";
import { useNavigate } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { haptic } from "@/lib/haptic";
import { useDictionaryStats } from "@/hooks/useDictionaryStats";
import { WordsBadge } from "@/ui";

/**
 * KnownWords — the header words badge.
 *
 * iOS ref: client-native/src/ui/molecules/WordsBadge.tsx — renders
 * "📖 {count} {label}" (book emoji, NOT the 🇪🇸 flag the PWA used before),
 * tapping navigates to the dictionary. Delegates entirely to the shared
 * `@/ui` WordsBadge so styling/label/formatNumber match iOS exactly.
 *
 * `theme` lets the home header render onDark over the sky and other headers
 * render onLight; defaults to onLight (the current light header chrome).
 */
interface KnownWordsProps {
  theme?: "onLight" | "onDark";
}

const KnownWords: React.FC<KnownWordsProps> = ({ theme = "onLight" }) => {
  const nav = useNavigate();
  const posthog = usePostHog();
  // iOS WordsBadge derives BOTH the count and the label from useDictionaryStats:
  // once total words >= 100 it shows the *known* count labelled "known", else the
  // total labelled "words" (single source of truth in the hook). The `known` flag
  // is what selects that label — without it a user past the threshold would see
  // the known count mislabelled as "words" (e.g. "14 words" instead of "14 known").
  const { badgeCount, showKnownBadge } = useDictionaryStats();

  const handleClick = () => {
    // iOS captures known_words_badge_clicked (with a platform property) on every
    // words-badge tap before navigating (useTrackKnownWordsBadgeClicked.tsx:7-9).
    posthog?.capture("known_words_badge_clicked", { platform: "web" });
    haptic.soft();
    nav("/home/dictionary");
  };

  return (
    <WordsBadge
      count={badgeCount}
      known={showKnownBadge}
      theme={theme}
      onClick={handleClick}
    />
  );
};

export default KnownWords;
